import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, InputNumber, Popconfirm, Select, Space, Table, message, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { usePricingApi, TPricing, TPricingUpsert } from '../../api/paiement/pricing';
import { useReferential } from '../../context/ReferentialContext';
import AppLayout from '../../components/layout/AppLayout';
import './Pricing.css';

const CURRENCY_OPTIONS = [
  { label: 'MAD', value: 'MAD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
  { label: 'GBP', value: 'GBP' },
];

const formatDate = (value: string | undefined) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    return d.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const api = usePricingApi();
  const { countries } = useReferential();
  const [form] = Form.useForm();
  const [items, setItems] = useState<TPricing[]>([]);
  const [loading, setLoading] = useState(false);

  const countryOptions = useMemo(
    () => countries.map((c) => ({ label: `${c.name} (${c.code})`, value: c.code })),
    [countries]
  );

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.list();
      setItems(data);
    } catch {
      message.error(t('pricing.loadError', 'Échec du chargement du pricing'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const onFinish = async (values: TPricingUpsert) => {
    setLoading(true);
    try {
      await api.upsert(values);
      message.success(t('pricing.saved', 'Tarif enregistré'));
      form.resetFields();
      await load();
    } catch {
      message.error(t('pricing.saveError', "Échec de l'enregistrement"));
    } finally {
      setLoading(false);
    }
  };

  // Le formulaire fait office d'éditeur : il upsert par pays, donc charger une
  // ligne dedans suffit à la modifier.
  const onEdit = (record: TPricing) => {
    form.setFieldsValue({
      codepays: record.codepays,
      prix: record.prix,
      devise: record.devise,
      taux_conversion: record.taux_conversion,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (record: TPricing) => {
    setLoading(true);
    try {
      await api.remove(record.id_pricing);
      message.success(t('pricing.deleted', 'Tarif supprimé'));
      await load();
    } catch {
      message.error(t('pricing.deleteError', 'Échec de la suppression'));
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<TPricing> = useMemo(
    () => [
      {
        title: t('pricing.country', 'Pays'),
        dataIndex: 'codepays',
        key: 'codepays',
        width: 160,
        sorter: (a, b) => (a.codepays || '').localeCompare(b.codepays || ''),
      },
      {
        title: t('pricing.price', 'Prix'),
        dataIndex: 'prix',
        key: 'prix',
        width: 140,
        align: 'right',
        render: (prix: number, record) => (
          <span className="pricing-cell-price">
            {typeof prix === 'number' ? prix.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : prix} {record.devise || ''}
          </span>
        ),
        sorter: (a, b) => (a.prix ?? 0) - (b.prix ?? 0),
      },
      {
        title: t('pricing.currency', 'Devise'),
        dataIndex: 'devise',
        key: 'devise',
        width: 100,
      },
      {
        title: t('pricing.rate', 'Taux → MAD'),
        dataIndex: 'taux_conversion',
        key: 'taux_conversion',
        width: 150,
        align: 'right',
        render: (taux: number | undefined, record) =>
          taux ? (
            <span className="pricing-cell-price">
              1 {record.devise} = {taux.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </span>
          ) : (
            <span className="pricing-cell-muted">—</span>
          ),
      },
      {
        title: t('pricing.updatedAt', 'Mis à jour'),
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 180,
        render: formatDate,
        sorter: (a, b) => new Date(a.updated_at || 0).getTime() - new Date(b.updated_at || 0).getTime(),
      },
      {
        title: t('pricing.actions', 'Actions'),
        key: 'actions',
        width: 170,
        render: (_, record) => (
          <Space size={4}>
            <Button type="link" size="small" onClick={() => onEdit(record)}>
              {t('pricing.edit', 'Modifier')}
            </Button>
            <Popconfirm
              title={t('pricing.deleteTitle', 'Supprimer ce tarif ?')}
              description={t(
                'pricing.deleteWarning',
                "Sans tarif, plus aucun paiement n'est possible pour ce pays."
              )}
              okText={t('pricing.deleteConfirm', 'Supprimer')}
              cancelText={t('pricing.cancel', 'Annuler')}
              okButtonProps={{ danger: true }}
              onConfirm={() => onDelete(record)}
            >
              <Button type="link" size="small" danger>
                {t('pricing.delete', 'Supprimer')}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  return (
    <AppLayout title={t('pricing.title', 'Tarifs par pays')} loading={loading}>
      <div className="pricing-page-container">
        <Card className="pricing-header-card" bordered={false}>
          <Typography.Title level={3} className="pricing-page-title">
            {t('pricing.title', 'Tarifs par pays')}
          </Typography.Title>
          <p className="pricing-page-description">
            {t('pricing.description', 'Définissez le prix par mois (ou par période) pour chaque pays. Ces tarifs sont utilisés sur la page Paiement.')}
          </p>
        </Card>

        <Card
          className="pricing-form-card"
          title={t('pricing.new', 'Ajouter ou modifier un tarif')}
          bordered={false}
        >
          <p className="pricing-form-hint">
            {t('pricing.formHint', 'Sélectionnez un pays, saisissez le prix et la devise. Si un tarif existe déjà pour ce pays, il sera mis à jour.')}
          </p>
          <p className="pricing-form-hint">
            {t(
              'pricing.rateHint',
              "Le paiement par carte est encaissé en dirhams : le taux convertit le prix du pays en MAD au moment du paiement (17,50 EUR × 10,85 = 189,88 MAD). Laissez 1 pour un pays déjà tarifé en MAD. Champ vide = taux inchangé ; 0 pour l'effacer, mais le paiement par carte sera alors refusé pour ce pays."
            )}
          </p>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ maxWidth: 560 }}
          >
            <Form.Item
              name="codepays"
              label={t('pricing.country', 'Pays')}
              rules={[{ required: true, message: t('pricing.requiredCountry', 'Choisissez un pays') }]}
            >
              <Select
                showSearch
                placeholder={t('pricing.selectCountry', 'Choisir un pays...')}
                options={countryOptions}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  (option?.label as string).toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Form.Item
                name="prix"
                label={t('pricing.price', 'Prix')}
                rules={[{ required: true, message: t('pricing.requiredPrice', 'Saisissez le prix') }]}
                style={{ flex: '1 1 160px', minWidth: 140 }}
              >
                <InputNumber
                  min={0.01}
                  step={0.5}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                />
              </Form.Item>
              <Form.Item
                name="devise"
                label={t('pricing.currency', 'Devise')}
                rules={[{ required: true, message: t('pricing.requiredCurrency', 'Choisissez une devise') }]}
                style={{ flex: '0 0 120px', minWidth: 100 }}
              >
                <Select
                  placeholder="MAD, EUR..."
                  options={CURRENCY_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
            <Form.Item
              name="taux_conversion"
              label={t('pricing.rate', 'Taux → MAD')}
              tooltip={t('pricing.rateTooltip', "Combien vaut 1 unité de la devise ci-dessus en dirhams")}
              style={{ maxWidth: 220 }}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="10.85"
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" className="pricing-save-btn" size="large">
                {t('pricing.save', 'Enregistrer le tarif')}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card
          className="pricing-table-card"
          title={t('pricing.list', 'Tarifs enregistrés')}
          bordered={false}
        >
          <Table<TPricing>
            className="pricing-table"
            rowKey="id_pricing"
            dataSource={items}
            columns={columns}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => t('pricing.totalTariffs', '{{total}} tarif(s)', { total }),
            }}
            loading={loading}
            locale={{
              emptyText: t('pricing.noTariffs', 'Aucun tarif. Ajoutez-en un avec le formulaire ci-dessus.'),
            }}
          />
        </Card>
      </div>
    </AppLayout>
  );
};

export default Pricing;
